import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, filter, merge } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { CurrencyWalletService } from 'src/app/layouts/currency-wallet/currency-wallet.service';

type CsCurrencyUnitDisplayType = 'symbol' | 'code' | 'icon';

enum CurrencyUnitDisplayType {
  /** 貨幣符號 */
  Symbol = 'symbol',

  /** 貨幣代碼 */
  Code = 'code',

  /** 貨幣圖示 */
  Icon = 'icon',
}

@UntilDestroy()
@Component({
  selector: 'cs-currency-unit-display',
  standalone: true,
  imports: [],
  templateUrl: './cs-currency-unit-display.component.html',
  styleUrl: './cs-currency-unit-display.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush, // ✨ 加這個
})
export class CsCurrencyUnitDisplayComponent implements OnChanges, OnInit {
  constructor(
    private appService: AppService,
    private currencyWalletService: CurrencyWalletService,
    private cdr: ChangeDetectorRef,
  ) {}

  /**貨幣類型 */
  @Input({ required: true }) currency!: string;

  /**是否將貨幣類型轉換為系統設定值 */
  @Input() isConvertToSystemSettingCurrency = false;

  /**數位貨幣顯示類型（貨幣符號/貨幣代碼） */
  @Input() digitalDisplayType: CsCurrencyUnitDisplayType = CurrencyUnitDisplayType.Icon;

  /**法幣貨幣顯示類型（貨幣符號/貨幣代碼） */
  @Input() fiatDisplayType: CsCurrencyUnitDisplayType = CurrencyUnitDisplayType.Code;

  /**貨幣圖標大小（px）  */
  @Input() iconSize = 16;

  protected readonly CurrencyUnitDisplayType = CurrencyUnitDisplayType;

  /**顯示類型 */
  protected displayType!: CsCurrencyUnitDisplayType;

  /**貨幣符號 */
  protected currencySymbol = '';

  /**貨幣代碼 */
  protected currencyCode = '';

  /**貨幣圖示 */
  protected currencyIcon = '';

  ngOnChanges(changes: SimpleChanges): void {
    const { currency } = changes;
    if (currency) {
      this._updateCurrencyUnit(currency.currentValue);
    }
  }

  ngOnInit(): void {
    merge(
      this.appService.currencies$,
      this.appService.currentCurrency$,
      this.appService.currentCurrency$.pipe(filter(() => this.isConvertToSystemSettingCurrency)),
      this.currencyWalletService.displayInFiat$,
      this.currencyWalletService.inFiatConvertType$,
    )
      .pipe(untilDestroyed(this), debounceTime(0))
      .subscribe(() => this._updateCurrencyUnit(this.currency));
  }

  /**更新貨幣單位 */
  private _updateCurrencyUnit(currency: string): void {
    let targetConversionCurrency: unknown = currency;
    if (this.isConvertToSystemSettingCurrency) {
      targetConversionCurrency = this.currencyWalletService.defaultToFiat$.value.currency?.currency;
    }
    if (this.currencyWalletService.displayInFiat$.value) {
      targetConversionCurrency = this.currencyWalletService.inFiatConvertType$.value;
    }
    if (!targetConversionCurrency) {
      console.warn(`[CsCurrencyConverterComponent] Invalid currency value: ${targetConversionCurrency}.`);
      return;
    }

    const allCurrencies = this.appService.currencies$.value;
    const displayInFiat = this.currencyWalletService.displayInFiat$.value;
    const currencyConfig = allCurrencies?.find(x => x?.currency === targetConversionCurrency);
    if (!currencyConfig) {
      console.warn(`[CsCurrencyConverterComponent] Currency configuration not found for: ${targetConversionCurrency}.`);
      return;
    }

    this.displayType = displayInFiat ? this.fiatDisplayType : this.digitalDisplayType;
    this.currencySymbol = currencyConfig?.symbol || '';
    this.currencyCode = currencyConfig?.currency || '';
    this.currencyIcon = currencyConfig?.icon || '';

    this.cdr.markForCheck();
  }
}
